import { ArrowType } from '@ardoq/api-types';
import { NO_MARKER, type NetworkMarker } from '@ardoq/xyz-charts';

/**
 * What a reference type's declared marker is, in the terms the 3D view draws in.
 *
 * The boundary between Ardoq's `ArrowType` and the package's own vocabulary, and the only place
 * that knows both. What decides each mapping is the 2D artwork in
 * `packages/icons/src/svgSpriteSymbols/Arrow*.svg` - the same glyphs the
 * `Visualizations / Notation standards` story snapshots - because the 3D marker has to be
 * recognisably the thing a reader already knows from every other view.
 *
 * A filled glyph becomes a solid body, a white-filled one the same body hollow, and anything drawn
 * as strokes alone stays flat and is billboarded. The `*_START` variants carry the same glyph; the
 * end it belongs to is the caller's business, not the shape's.
 */
const MARKERS: Record<ArrowType, NetworkMarker> = {
  [ArrowType.NONE]: NO_MARKER,

  /* Filled, so a solid body. */
  [ArrowType.BOTH_FILLED]: { kind: 'solid', body: 'cone', isHollow: false },
  [ArrowType.BOTH_FILLED_START]: {
    kind: 'solid',
    body: 'cone',
    isHollow: false,
  },
  [ArrowType.DIAMOND_FILLED]: {
    kind: 'solid',
    body: 'octahedron',
    isHollow: false,
  },
  [ArrowType.DIAMOND_FILLED_START]: {
    kind: 'solid',
    body: 'octahedron',
    isHollow: false,
  },
  [ArrowType.CIRCLE]: { kind: 'solid', body: 'sphere', isHollow: false },
  [ArrowType.CIRCLE_START]: { kind: 'solid', body: 'sphere', isHollow: false },

  /* White-filled with a dark stroke, so the same body hollow. */
  [ArrowType.BOTH_OUTLINED]: { kind: 'solid', body: 'cone', isHollow: true },
  [ArrowType.BOTH_OUTLINED_START]: {
    kind: 'solid',
    body: 'cone',
    isHollow: true,
  },
  [ArrowType.DIAMOND]: { kind: 'solid', body: 'octahedron', isHollow: true },
  [ArrowType.DIAMOND_START]: {
    kind: 'solid',
    body: 'octahedron',
    isHollow: true,
  },

  /* Strokes with no interior. Flat notation, billboarded. */
  [ArrowType.DEFAULT]: { kind: 'planar', glyph: 'slash' },
  [ArrowType.DEFAULT_START]: { kind: 'planar', glyph: 'slash' },
  [ArrowType.BOTH]: { kind: 'planar', glyph: 'chevron' },
  [ArrowType.BOTH_START]: { kind: 'planar', glyph: 'chevron' },
  [ArrowType.TOP]: { kind: 'planar', glyph: 'halfTop' },
  [ArrowType.TOP_START]: { kind: 'planar', glyph: 'halfTop' },
  [ArrowType.BOTTOM]: { kind: 'planar', glyph: 'halfBottom' },
  [ArrowType.BOTTOM_START]: { kind: 'planar', glyph: 'halfBottom' },
  [ArrowType.ONE]: { kind: 'planar', glyph: 'one' },
  [ArrowType.ONE_START]: { kind: 'planar', glyph: 'one' },
  [ArrowType.ONE_MANDATORY]: { kind: 'planar', glyph: 'oneMandatory' },
  [ArrowType.ONE_MANDATORY_START]: { kind: 'planar', glyph: 'oneMandatory' },
  [ArrowType.MANY]: { kind: 'planar', glyph: 'many' },
  [ArrowType.MANY_START]: { kind: 'planar', glyph: 'many' },
  [ArrowType.ONE_OR_MANY]: { kind: 'planar', glyph: 'oneOrMany' },
  [ArrowType.ONE_OR_MANY_START]: { kind: 'planar', glyph: 'oneOrMany' },
  [ArrowType.ZERO_OR_ONE]: { kind: 'planar', glyph: 'zeroOrOne' },
  [ArrowType.ZERO_OR_ONE_START]: { kind: 'planar', glyph: 'zeroOrOne' },
  [ArrowType.ZERO_OR_MANY]: { kind: 'planar', glyph: 'zeroOrMany' },
  [ArrowType.ZERO_OR_MANY_START]: { kind: 'planar', glyph: 'zeroOrMany' },
};

/**
 * The marker a reference type's `lineBeginning` or `lineEnding` stands for.
 *
 * Narrow enough that the record above decides it. Taking a `string` and casting made that
 * exhaustiveness useless: the cast let every unknown spelling fall through a fallback the real
 * types can never reach, and a caller handing over a stray string still compiled.
 *
 * Which end a marker belongs at is not this function's business. A reference type sets the two
 * fields separately and either may hold either spelling, so the field a value came from is what
 * says which end it decorates - never the suffix on its name.
 */
export const toNetworkMarker = (
  arrowType: ArrowType | undefined
): NetworkMarker => (arrowType === undefined ? NO_MARKER : MARKERS[arrowType]);
